// §17–§18 Applicant home — all data is live: getApplicantHome(reference)
// Tracks the application through submission, verification and approval. Once
// approved, the customer receives a temporary password rather than an activation link.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { getApplicantHome, type ApplicantHomeData } from '@/api/applicant';
import {
  getMockVerifiedAccount,
  isMockVerified,
  startMockSession,
} from '@/api/activation';
import { approveApplication, autoVerifyApplication } from '@/api/review';
import { DEV_AUTO_VERIFY, MOCK_BANK_VERIFICATION } from '@/api/flags';
import { getSession } from '@/api/session';
import { ApiError } from '@/api/client';
import './onboarding.css';

const STAGES = ['Submitted', 'Video KYC', 'Bank verification', 'Active'] as const;

const ACTIVE_STATES = ['ACTIVE', 'ACTIVATED', 'ACTIVE_FULL_KYC', 'ACTIVE_LIMITED_KYC'];

const isActive = (status: string): boolean => ACTIVE_STATES.includes(status.toUpperCase());

/** 0 Submitted · 1 Video KYC · 2 Bank verification · 3 Active */
const stageIndex = (data: ApplicantHomeData): number => {
  const status = data.application.status.toUpperCase();
  if (isActive(status)) return 3;
  const vkyc = data.videoKyc?.state;
  if (vkyc === 'DONE_AUDIT_PENDING' || vkyc === 'APPROVED') return 2;
  // This page is only reachable after step 8, so the application is always
  // submitted: Video KYC is the live stage — whether it is not started yet
  // (NOT_STARTED / null), booked (SCHEDULED), in a call (IN_PROGRESS) or
  // needs a redo (FAILED / REJECTED). Join and schedule are self-service
  // (POST .../video-kyc/join | /schedule), so this stage is never disabled.
  return 1;
};

const fmtDateTime = (iso?: string | null): string => {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const ApplicantHome = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const session = getSession();
  const [data, setData] = useState<ApplicantHomeData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [verifyError, setVerifyError] = useState('');
  // The server does not expose temporary-password delivery yet; the dev flow
  // keeps the delivery action local until that endpoint is released.
  const autoVerifyFired = useRef(false);

  const load = useCallback(() => {
    if (!session?.reference) return Promise.resolve();
    return getApplicantHome(session.reference)
      .then((home) => setData(home))
      .catch((err) => {
        setError(
          err instanceof ApiError ? err.message : 'Could not load your application. Please try again.'
        );
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    load().finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [load]);

  // Poll while the application is still moving so a video-KYC sign-off or the
  // bank's approval shows up without a manual reload. Stops once the account
  // is active (really or via the dev dummy approval) or turned down.
  useEffect(() => {
    const s = (data?.application.status ?? '').toUpperCase();
    const mockDone =
      MOCK_BANK_VERIFICATION && !!session?.reference && isMockVerified(session.reference);
    const settled = !data || isActive(s) || s === 'REJECTED' || mockDone;
    if (settled) return;
    const timer = setInterval(() => void load(), 10_000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, load]);

  const refreshNow = async () => {
    setRefreshing(true);
    setError('');
    // Re-arm the dev auto-verify so a fixed token / backend is retried now.
    autoVerifyFired.current = false;
    setVerifyError('');
    await load();
    setRefreshing(false);
  };

  // Dev: when the application is waiting at "Bank verification", approve it —
  // a local dummy approval when MOCK_BANK_VERIFICATION is on (no backend call,
  // no employee token), otherwise the real review chain (DEV_AUTO_VERIFY).
  useEffect(() => {
    if (!data || autoVerifyFired.current) return;
    const s = data.application.status.toUpperCase();
    if (isActive(s) || s === 'REJECTED') return;
    const vk = data.videoKyc?.state;
    const waitingForBank = vk === 'DONE_AUDIT_PENDING' || vk === 'APPROVED';
    if (!waitingForBank) return;
    autoVerifyFired.current = true;
    if (!DEV_AUTO_VERIFY) return;
    if (MOCK_BANK_VERIFICATION) {
      approveApplication(data.application.applicationId)
        .then(() => {
          const mockAccount = session?.reference ? getMockVerifiedAccount(session.reference) : null;
          if (mockAccount) {
            startMockSession(mockAccount);
            navigate('/dashboard', { replace: true });
            return;
          }
          return load();
        })
        .catch((err) => {
          setVerifyError(`Verification failed: ${err instanceof Error ? err.message : 'unknown error'}`);
        });
      return;
    }
    autoVerifyApplication(data.application.applicationId)
      .then(() => {
        setVerifyError('');
        return load();
      })
      .catch((err) => {
        const apiErr = err instanceof ApiError ? err : null;
        const authProblem = apiErr?.status === 401 || apiErr?.status === 403;
        setVerifyError(
          authProblem
            ? 'Verification needs an employee token — paste one in the dev test panel (Bank desk), then this continues on its own.'
            : `Verification failed: ${apiErr ? `${apiErr.message} (${apiErr.code})` : 'unknown error'}`
        );
        // Keep retrying on the poll only for fixable causes (missing token,
        // network). Anything else waits for "Check again" to re-arm.
        if (authProblem || apiErr?.code === 'NETWORK') {
          autoVerifyFired.current = false;
        }
      });
  }, [data, load]);

  const netBankingJustSaved = (location.state as { netBankingSaved?: boolean } | null)?.netBankingSaved;

  const status = (data?.application.status ?? '').toUpperCase();
  const realActive = isActive(status);
  // Dev dummy approval: treated as active locally even though the backend
  // still shows the pre-approval status. A real ACTIVE status always wins.
  const mockVerified =
    MOCK_BANK_VERIFICATION && !realActive && !!session?.reference && isMockVerified(session.reference);
  const active = realActive || mockVerified;
  const rejected = status === 'REJECTED';
  const vkycState = data?.videoKyc?.state ?? null;
  const vkycDone = vkycState === 'DONE_AUDIT_PENDING' || vkycState === 'APPROVED' || active;
  const vkycRedo = !active && (vkycState === 'FAILED' || vkycState === 'REJECTED');
  const vkycBooked = !active && (vkycState === 'SCHEDULED' || vkycState === 'IN_PROGRESS');
  const current = mockVerified ? 3 : data ? stageIndex(data) : 0;

  const stageLabel = useMemo(() => {
    if (rejected) return 'Turned down';
    if (active) return 'Account active';
    if (vkycDone) return 'Verifying bank details';
    if (vkycRedo) return 'Video KYC to redo';
    if (vkycBooked) return 'Video KYC booked';
    return STAGES[current];
  }, [rejected, active, vkycDone, vkycRedo, vkycBooked, current]);

  if (!session?.reference) {
    return <Navigate to="/login" replace />;
  }

  if (loading) {
    return (
      <div className="nova-ob-page">
        <span className="spinner-border text-primary" aria-hidden="true" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="nova-ob-page">
        <div className="nova-ob-panel nova-ob-panel-narrow text-center">
          <h1 className="nova-ob-heading">NovaBank</h1>
          <p className="text-muted mb-3">{error || 'No application found.'}</p>
          <button type="button" className="btn btn-primary" onClick={() => navigate('/welcome')}>
            Back to start
          </button>
        </div>
      </div>
    );
  }

  const { application, videoKyc, digitalBanking } = data;
  const videoKycTime = fmtDateTime(videoKyc?.scheduledAt);

  return (
    <div className="nova-ob-page nova-applicant-page">
      <div className="nova-ob-header">
        <h1>Hello, {application.firstName}</h1>
        <p>Here is where your application stands.</p>
      </div>

      <div className="nova-ob-body">
        {/* Status tracker — 4 stages */}
        <div className="nova-ob-panel mb-3">
          {STAGES.map((label, i) => {
            const cls = i < current ? 'done' : i === current ? 'current' : '';
            return (
              <div key={label}>
                <div className="nova-ob-tracker-step">
                  <span className={`nova-ob-tracker-dot ${cls}`}>{i < current ? '✓' : i + 1}</span>
                  <span className={i === current ? 'fw-semibold' : ''}>{label}</span>
                </div>
                {i < STAGES.length - 1 && <div className={`nova-ob-tracker-line${i < current ? ' done' : ''}`} />}
              </div>
            );
          })}
          <p className="text-muted text-center mt-2 mb-0" style={{ fontSize: '0.85rem' }}>
            {stageLabel}
          </p>
        </div>

        {netBankingJustSaved && (
          <div className="alert alert-success py-2" role="status">
            Your services are saved.
          </div>
        )}
        {error && (
          <div className="alert alert-danger py-2" role="alert">
            {error}
          </div>
        )}

        {/* Rejected */}
        {rejected && (
          <div className="nova-ob-status red">
            <h2>Your application was turned down</h2>
            <p className="mb-0">
              The bank could not open your account. Please visit a branch with your original documents, or call
              1800 425 3800 (toll free).
            </p>
          </div>
        )}

        {/* Video KYC card */}
        {!rejected && !vkycDone && (
          <div className="nova-ob-panel mb-3">
            <h2 className="nova-ob-section-title">Video KYC</h2>
            {vkycRedo ? (
              <>
                <p className="text-muted">
                  {videoKyc?.rejectionReason
                    ? `The bank has asked you to redo your video KYC: ${videoKyc.rejectionReason}`
                    : 'The bank has asked you to redo your video KYC.'}
                </p>
                <button
                  type="button"
                  className="btn btn-primary w-100"
                  onClick={() => navigate('/applicant/video-kyc')}
                >
                  Restart now
                </button>
              </>
            ) : vkycBooked ? (
              <>
                <p className="text-muted">
                  {vkycState === 'IN_PROGRESS'
                    ? 'Your video KYC call is in progress.'
                    : videoKycTime
                      ? `Your video KYC is booked for ${videoKycTime}. Open it a few minutes before your time.`
                      : 'Your video KYC is booked.'}
                </p>
                <button
                  type="button"
                  className="btn btn-primary w-100"
                  onClick={() => navigate('/applicant/video-kyc')}
                >
                  Open video KYC
                </button>
              </>
            ) : (
              <>
                <p className="text-muted">
                  A 3 to 5 minute video call with a NovaBank officer. Keep your original PAN, Aadhaar, a blank
                  white sheet and a pen ready — you can start right away or pick a slot.
                </p>
                <button
                  type="button"
                  className="btn btn-primary w-100"
                  onClick={() => navigate('/applicant/video-kyc')}
                >
                  Complete video KYC
                </button>
              </>
            )}
          </div>
        )}

        {/* Waiting for activation after a clean video KYC */}
        {!rejected && vkycDone && !active && (
          <div className="nova-ob-panel mb-3">
            <h2 className="nova-ob-section-title">Bank verification</h2>
            <p className="text-muted">
              Your video KYC is done. The bank is verifying your details and opening your account — this page
              checks automatically and will move on as soon as it's approved. You'll also get an SMS and an email.
            </p>
            {verifyError && (
              <div className="alert alert-warning py-2" role="alert">
                {verifyError}
              </div>
            )}
            <button
              type="button"
              className="btn btn-outline-primary w-100"
              disabled={refreshing}
              onClick={() => void refreshNow()}
            >
              {refreshing && <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />}
              Check again
            </button>
          </div>
        )}

        {/* Active — temporary password delivery or log in */}
        {active && (
          <div className="nova-ob-status green">
            <h2>Your account is active</h2>
            <div className="nova-ob-status-grid">
              {application.accountNumber && (
                <div className="nova-ob-status-item">
                  <span>Account number</span>
                  <strong>{application.accountNumber}</strong>
                </div>
              )}
              {application.ifsc && (
                <div className="nova-ob-status-item">
                  <span>IFSC</span>
                  <strong>{application.ifsc}</strong>
                </div>
              )}
              {application.branchName && (
                <div className="nova-ob-status-item">
                  <span>Branch</span>
                  <strong>{application.branchName}</strong>
                </div>
              )}
            </div>
            <button
              type="button"
              className="btn btn-outline-light w-100"
              onClick={() => {
                const mockAccount = MOCK_BANK_VERIFICATION && session.reference
                  ? getMockVerifiedAccount(session.reference)
                  : null;
                if (mockAccount) {
                  startMockSession(mockAccount);
                  navigate('/dashboard');
                } else {
                  navigate('/login');
                }
              }}
            >
              Log in now
            </button>
          </div>
        )}



        {/* Net banking — only once the account is really active (the dummy
            approval has no account on the backend to configure). */}
        {realActive && (
          <div className="nova-ob-panel mb-3">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h2 className="nova-ob-section-title mb-1">Net banking & services</h2>
                <p className="text-muted mb-0">
                  {digitalBanking?.digitalEnabled ? 'Active ✓' : 'Not set up yet'}
                </p>
              </div>
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={() => navigate('/applicant/net-banking')}
              >
                {digitalBanking?.digitalEnabled ? 'Review' : 'Set up'}
              </button>
            </div>
          </div>
        )}

        <p className="text-muted text-center mt-4" style={{ fontSize: '0.85rem' }}>
          Questions? Call 1800 425 3800 (toll free).
        </p>
      </div>
    </div>
  );
};

export default ApplicantHome;
